import { Body, Card, Title, VAlert, VBadge, VStatusIcon } from '@vrzl/ui';
import { AlertTriangle, FileText } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import type { CaseDetail } from '../api/types';
import {
  extractionStatusIcon,
  extractionStatusLabels,
  statusBadgeColor,
  statusLabels,
} from '../constants';

import { ExtractionStatusTooltip } from './extractionStatusTooltip';
import { RxMissionLink } from './rxMissionLink';

type EventHeaderProps = {
  event: CaseDetail;
  documents: ReadonlyArray<{ indexingStatus: string }>;
  /** Rendered top-right, next to the title and badges. */
  actions?: ReactNode;
  /** Hide the "Bekijk documenten" link, e.g. on the documents page itself. */
  hideDocumentsLink?: boolean;
};

export function EventHeader({
  event,
  documents,
  actions,
  hideDocumentsLink = false,
}: EventHeaderProps) {
  const documentCount = documents.length;
  const notIndexedCount = documents.filter((d) => d.indexingStatus !== 'INDEXED').length;

  return (
    <>
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Title>{event.name}</Title>
          <VBadge color={statusBadgeColor[event.status]}>{statusLabels[event.status]}</VBadge>
          {event.extractionStatus && (
            <ExtractionStatusTooltip status={event.extractionStatus} error={event.extractionError}>
              <VStatusIcon
                {...extractionStatusIcon[event.extractionStatus]}
                label={extractionStatusLabels[event.extractionStatus]}
              />
            </ExtractionStatusTooltip>
          )}
          {actions && <div className="ml-auto flex items-center gap-3">{actions}</div>}
        </div>
        <RxMissionLink externalId={event.externalId} className="mt-1" />
      </div>

      {notIndexedCount > 0 && (
        // [&>div]:flex-1: VAlert's content wrapper takes the full width, so the link sits on the right.
        <VAlert variant="warning" className="mb-4 items-center py-3 [&>div]:flex-1">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <AlertTriangle className="size-4 shrink-0 text-warning-600" aria-hidden="true" />
            <p className="min-w-0 flex-1">
                {notIndexedCount === 1
                  ? '1 document is nog niet verwerkt.'
                  : `${notIndexedCount} documenten zijn nog niet verwerkt.`}
            </p>
            {!hideDocumentsLink && (
              <Link
                // status=FAILED_WITH_ERRORS: keep in sync with DOCUMENT_FILTER_PARAM in the documents domain.
                to={`/events/${event.id}/documents?status=FAILED_WITH_ERRORS`}
                className="shrink-0 font-semibold underline underline-offset-2 hover:text-warning-900 focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                Bekijk documenten
              </Link>
            )}
          </div>
        </VAlert>
      )}

      <Card padding="md" className="mb-4">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {event.location && (
            <Body as="span" tone="muted">
              <span className="font-medium text-foreground">Locatie:</span> {event.location}
            </Body>
          )}
          <Body as="span" tone="muted">
            <span className="font-medium text-foreground">Start:</span>{' '}
            {new Date(event.startedOn).toLocaleDateString('nl-NL')}
          </Body>
          <Body as="span" tone="muted">
            <span className="font-medium text-foreground">Toegewezen:</span>{' '}
            {event.assignee ?? 'Niet toegewezen'}
          </Body>
          <Body as="span" tone="muted">
            <FileText className="mr-1 inline size-4" />
            {documentCount} {documentCount === 1 ? 'document' : 'documenten'}
          </Body>
        </div>
      </Card>
    </>
  );
}
